import { Autocomplete, Box, Grid, Popper, TextField } from '@mui/material';
import { locationData as locationDataAtom, locationDataList } from 'atoms';
import axios from 'axios';
import FormHeading from 'common/FormHeading';
import FormInputLabel from 'common/FormInputLabel';
import FormTextInput from 'common/FormTextInput';
import FormTextInputPhoneMask from 'common/FormTextInputPhoneMask';
import { InlineLoader } from 'common/Loaders/Loader';
import { fetchPlaceDetails, parsePredictionTerms } from 'utils/placeDetails';
import { clearSessionToken, getSessionToken } from 'utils/placesSession';
import { useRecoilState } from 'recoil';

import React, { useEffect, useState, useRef } from 'react';

import { useAuth } from 'context/AuthContext';
const LocationFormData = ({ locationsCount, setLocationCount, isCreating = false }) => {
    const { token, loading } = useAuth();
   
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [addressPredictions, setAddressPredictions] = useState([]);
    const [formData, setFormData] = useRecoilState(locationDataList);
    // State to track dropdown open status
    const [isDropdownOpen, setIsDropdownOpen] = useState({});
    // Track if user has started typing
    const [hasStartedTyping, setHasStartedTyping] = useState({});
    // Reference to location field
    const locationFieldRef = useRef({});
    // Newest place_id awaiting a details lookup, per location row
    const pendingPlaceRef = useRef({});
    // Google Places billing session per location row (see utils/placesSession)
    const sessionTokenRef = useRef({});
    // Debounce timers for the autocomplete lookup, per location row
    const searchTimersRef = useRef({});
    // Rows whose Place Details lookup is still in flight. City/state/zip are
    // cleared as soon as a suggestion is picked, so without this the fields
    // just vanish until the response lands.
    const [isResolvingAddress, setIsResolvingAddress] = useState({});

    const setRowResolving = (index, value) =>
        setIsResolvingAddress((prev) => ({ ...prev, [index]: value }));

    // Drop any in-flight debounce timers when the form unmounts
    useEffect(() => {
        const timers = searchTimersRef.current;
        return () => {
            Object.values(timers).forEach((timer) => clearTimeout(timer));
        };
    }, []);
    
    useEffect(() => {
        setFormData((prevData) => {
            const newData = [...prevData];

            while (newData.length < locationsCount) {
                newData.push(locationDataAtom);
            }

            return newData.slice(0, locationsCount);
        });
    }, [locationsCount, setFormData]);
    
    // Effect to handle scrolling when user types in address field
    useEffect(() => {
        const activeIndex = Object.keys(hasStartedTyping).find(
            index => hasStartedTyping[index] && isDropdownOpen[index]
        );
        
        if (activeIndex && isCreating && locationFieldRef.current[activeIndex]) {
            // Find the modal container - the nearest parent with overflow:auto
            const modalContainer = document.querySelector('#form-drawer .MuiBox-root');
            
            if (modalContainer) {
                setTimeout(() => {
                    // Calculate the position to scroll to (field position + extra space for dropdown)
                    const field = locationFieldRef.current[activeIndex];
                    const fieldRect = field.getBoundingClientRect();
                    const currentScroll = modalContainer.scrollTop;
                    const targetPosition = currentScroll + fieldRect.bottom - modalContainer.clientHeight + 250;
                    
                    // Smooth scroll to position
                    modalContainer.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }, 100);
            }
        }
    }, [hasStartedTyping, isDropdownOpen, isCreating]);

    const handleChange = (index, e) => {
        const { name, value } = e.target;
    
        setFormData((prevData) => {
            const newData = [...prevData];
    
            newData[index] = {
                ...newData[index],
                [name]: name === "zipCode" ? value.replace(/\D/g, '') : value,
                ...(name === "location_address" && value === "" ? { state: "", zipCode: "", suite: "", city: "" } : {}), // Reset fields if address is cleared
            };
    
            return newData;
        });
    
        if (name === 'location_address') {
            // Track that user has started typing in this field
            if (value.length > 0 && !hasStartedTyping[index]) {
                setHasStartedTyping(prev => ({
                    ...prev,
                    [index]: true
                }));
            } else if (value.length === 0) {
                setHasStartedTyping(prev => ({
                    ...prev,
                    [index]: false
                }));
            }
            
            // Debounce the lookup. The previous `charCount % 3 === 0` throttle
            // fired on arbitrary lengths — and while deleting — so the
            // suggestion list could lag behind what was actually typed. It also
            // billed ~8 autocomplete requests for a typical address.
            if (searchTimersRef.current[index]) {
                clearTimeout(searchTimersRef.current[index]);
            }
            searchTimersRef.current[index] = setTimeout(() => {
                getAddresses(value, index).then(() => {});
            }, 300);
        }
    };
    // Applies resolved address fields onto one location row.
    const applyAddressFields = (index, fields) => {
        setFormData((prevData) => {
            const newData = [...prevData];

            newData[index] = {
                ...newData[index],
                location_address: fields.location_address || '',
                city: fields.city || '',
                state: fields.state || '',
                zipCode: fields.zipCode || '',
                // `suite` is only present when Place Details returned a
                // subpremise; keep whatever the user typed otherwise.
                suite: fields.suite ?? newData[index]?.suite ?? '',
            };

            return newData;
        });

        // Reset typing state since selection is complete
        setHasStartedTyping((prev) => ({
            ...prev,
            [index]: false,
        }));
    };

    const handleChangePredictedData = async (e, value, index) => {
        let prediction = addressPredictions.find((item) => item.description === value);

        // Clear suggestions and predictions
        setAddressPredictions([]);
        setAddressSuggestions([]);

        // Free-text entry that matches no prediction: keep what was typed and
        // leave the other fields alone rather than blanking them.
        if (!prediction) {
            setRowResolving(index, false);
            setFormData((prevData) => {
                const newData = [...prevData];
                newData[index] = {
                    ...newData[index],
                    location_address: typeof value === 'string' ? value : '',
                };
                return newData;
            });
            return;
        }

        // Clear previous location data while the lookup is in flight, so stale
        // city/state/zip are never left sitting under a new address.
        setFormData((prevData) => {
            const newData = [...prevData];

            newData[index] = {
                ...newData[index],
                location_address: '',
                city: '',
                state: '',
                zipCode: '',
                suite: ''
            };

            return newData;
        });

        // Mark this as the newest selection for the row so a slow response for
        // an earlier selection cannot overwrite a later one.
        pendingPlaceRef.current[index] = prediction.place_id;
        setRowResolving(index, true);

        // Read the session token before clearing it: the details call must carry
        // the same one the autocomplete requests used, and the session ends here.
        const sessionToken = getSessionToken(sessionTokenRef.current, index);
        clearSessionToken(sessionTokenRef.current, index);

        try {
            const fields = await fetchPlaceDetails(prediction.place_id, token, sessionToken);

            if (pendingPlaceRef.current[index] !== prediction.place_id) {
                return;
            }

            setRowResolving(index, false);
            applyAddressFields(index, {
                ...fields,
                // Place Details omits street_number for some records; fall back
                // to the prediction's own street line rather than showing blank.
                location_address:
                    fields.location_address || prediction?.structured_formatting?.main_text || '',
            });
        } catch (error) {
            console.error('Place details lookup failed, falling back to prediction terms:', error);

            if (pendingPlaceRef.current[index] !== prediction.place_id) {
                return;
            }

            setRowResolving(index, false);
            applyAddressFields(index, parsePredictionTerms(prediction));
        }
    };

    const getAddresses = async (address, index) => {
        const serverUrl = process.env.REACT_APP_SERVER_URL ;

        if (!address || address.trim().length < 3) {
            return;
        }

        // Wait for auth to finish loading before checking token
        if (loading) {
            return;
        }

        if (!token) {
            console.error('No authentication token found');
            return;
        }

        // Same token for every keystroke of this address entry, then handed to
        // the details lookup on selection, which makes these calls free.
        const sessionToken = getSessionToken(sessionTokenRef.current, index);

        axios.get(
            // Encoded so a '#' in an address is not read as a URL fragment.
            `${serverUrl}/places/${encodeURIComponent(address)}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              params: { sessiontoken: sessionToken },
            }
          )
          .then((response) => {
            setAddressSuggestions(
              response.data.predictions.map((item) => item.description)
            );
            setAddressPredictions(response.data.predictions);
          })
          .catch((error) => {
            console.error(error);
          });
          
    };
    const handleRemoveLocation = (index) => {
        setFormData((prevData) => {
            const newData = [...prevData];
            newData.splice(index, 1);
            return newData;
        });
        setLocationCount(locationsCount - 1);
    };

    // Handle dropdown open/close
    const handleDropdownToggle = (index, isOpen) => {
        setIsDropdownOpen(prev => ({
            ...prev,
            [index]: isOpen
        }));
    };

    if (!formData) {
        return;
    }
    return [...Array(locationsCount)].map((_, index) => {
        return (
            <React.Fragment key={index}>
                {index >= 1 && (
                    <div style={{ paddingTop: '25px' }}>
                        <FormHeading className="location-heading">Location {index + 1}</FormHeading>
                    </div>
                )}

                <Grid container spacing={2}>
                    <FormTextInput
                        formData={formData}
                        value={formData[index]?.location_name}
                        label="Location Name"
                        name="location_name"
                        handleChange={(e) => handleChange(index, e)}
                        // handleChange={handleChange}
                        placeholder="Enter your location name"
                        required={!isCreating}
                        labelSize={12}
                        textSize={12}
                    />

                    <FormTextInputPhoneMask
                        formData={formData}
                        label="Location Phone Number"
                        name="location_phone_no"
                        value={formData[index]?.location_phone_no}
                        // handleChange={handleChange}
                        handleChange={(e) => handleChange(index, e)}
                        placeholder="Enter location phone number"
                        required={!isCreating}
                        labelSize={12}
                        textSize={12}
                    />

                    <FormInputLabel label={'Location Address'} labelSize={6} />
                    <Grid item xs={12} ref={el => {
                        if (el) {
                            locationFieldRef.current[index] = el;
                        }
                    }}>
                    <Box sx={{ 
                        minHeight: isCreating && isDropdownOpen[index] && hasStartedTyping[index] ? '180px' : 'auto', 
                        transition: 'min-height 0.3s ease',
                    }}>
                        <Autocomplete
                        freeSolo
                        id={`location_address_${index}`}
                        disableClearable
                        onOpen={() => isCreating && handleDropdownToggle(index, true)}
                        onClose={() => isCreating && handleDropdownToggle(index, false)}
                        onChange={(e, value) => {
                            // Ensure only the formatted string is used
                            handleChangePredictedData(e, value, index);
                        }}
                        onInputChange={(e, newInputValue) => {
                            // Optional: update input value manually if needed
                            handleChange(index, {
                            target: {
                                name: 'location_address',
                                value: newInputValue,
                            }
                            });
                        }}
                        options={addressSuggestions}
                        value={formData[index]?.location_address ?? ''}
                        getOptionLabel={(option) => {
                            // If option is an object, extract label safely
                            return typeof option === 'string' ? option : option.label || '';
                        }}
                        filterOptions={(x) => x} // keep it as-is if you're customizing suggestions
                        sx={{
                            '& .MuiAutocomplete-paper': {
                                overflow: 'hidden',
                            },
                            '& .MuiAutocomplete-listbox': {
                                padding: 0,
                            },
                            '& .MuiAutocomplete-option': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                        PopperComponent={(props) => (
                            <Popper 
                                {...props} 
                                placement="bottom-start" 
                                style={{ 
                                    position: 'relative',
                                    width: props.anchorEl?.offsetWidth || 'auto'
                                }}
                                modifiers={[
                                    {
                                        name: 'preventOverflow',
                                        enabled: true,
                                        options: {
                                            boundary: 'viewport',
                                        },
                                    },
                                    {
                                        name: 'flip',
                                        enabled: false,
                                    },
                                ]}
                            />
                        )}
                        renderInput={(params) => (
                            <>
                            <TextField
                                fullWidth
                                name={'location_address'}
                                variant={'outlined'}
                                onChange={(e) => handleChange(index, e)}
                                {...params}
                                placeholder={'Start typing locations address'}
                                sx={{
                                '& .MuiOutlinedInput-input::placeholder': {
                                    color: '#4A4A4A',
                                    opacity: 1,
                                },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    transition: 'all 0.3s ease',
                                    minHeight: '44px',
                                    padding: 0,
                                    backgroundColor: '#f5f1ed',
                                    border: '1px solid #cbc1b7',
                                    '&:hover': {
                                        borderColor: '#ff5f39',
                                        backgroundColor: 'white',
                                    },
                                    '&.Mui-focused': {
                                        borderColor: '#ff5f39',
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 2px rgba(255, 95, 57, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '& .MuiAutocomplete-input': {
                                    padding: '10px 20px !important',
                                    color: '#000000',
                                },
                                }}
                            />
                            {index >= 1 && formData?.[index]?.location_address?.length === 0 && (
                                <Box
                                className="danger-link float-end"
                                onClick={() => handleRemoveLocation(index)}
                                sx={{ cursor: 'pointer', color: 'red', mt: 1 }}
                                >
                                - Remove This Location
                                </Box>
                            )}
                            </>
                        )}
                        />
                    </Box>
                  </Grid>

                    {isResolvingAddress[index] && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '16px 0',
                                }}
                            >
                                <InlineLoader size={24} thickness={4} />
                            </Box>
                        </Grid>
                    )}

                    {formData[index]?.city && (
                        <FormTextInput
                            formData={formData}
                            disabled={true}
                            label="City"
                            name="city"
                            value={formData[index].city}
                            handleChange={() => {}}
                            placeholder="Enter City"
                            required={!isCreating}
                            labelSize={12}
                            noLabel={true}
                            textSize={6}
                            style={{ marginBottom: '0px' }}
                        />
                    )}

                    {formData[index]?.state && (
                        <FormTextInput
                            formData={formData}
                            disabled={true}
                            label="State"
                            name="state"
                            value={formData[index]?.state}
                            handleChange={() => {}}
                            placeholder="Enter state"
                            required={!isCreating}
                            labelSize={12}
                            noLabel={true}
                            textSize={2}
                            style={{ marginBottom: '0px' }}
                        />
                    )}

                    {formData[index]?.state && ( //adding state here because for most places we don't get zipcode
                        <FormTextInput
                            formData={formData}
                            disabled={false}
                            label="Zip Code"
                            name="zipCode"
                            value={formData[index]?.zipCode}
                            handleChange={(e) => handleChange(index, e)}
                            placeholder="Enter zip code"
                            required={!isCreating}
                            labelSize={12}
                            noLabel={true}
                            textSize={4}
                            style={{ marginBottom: '0px' }}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                            }}
                        />
                    )}

                    {formData[index]?.state && ( // adding state here because we've to show this field only when other (city,state,zip) are visible.
                        <FormTextInput
                            formData={formData}
                            disabled={false}
                            label="Suite"
                            name="suite"
                            value={formData[index]?.suite}
                            handleChange={(e) => handleChange(index, e)}
                            placeholder="Enter suite"
                            required={false}
                            labelSize={12}
                            noLabel={true}
                            textSize={12}
                            style={{ marginTop: '10px', marginBottom: '0px' }}
                            additionAction={
                                index >= 1 && (
                                    <Box
                                        className={'danger-link float-end w-full'}
                                        sx={{ cursor: 'pointer', color: 'red', mt: 1, textAlign: 'right' }}
                                        onClick={() => handleRemoveLocation(index)}
                                    >
                                        - Remove This Location
                                    </Box>
                                )
                            }
                        />
                    )}
                </Grid>
            </React.Fragment>
        );
    });
};

export default LocationFormData;
